/**
 * ``useVersions`` — the snapshot history of the active brief, for the Versions
 * tab. Mirrors `use-case-sources.ts`: reads the active context from
 * `useCaseStore` (no new store, W5), calls the `caseList.listSnapshots` port,
 * returns `{ snapshots, loading, error, load }`.
 *
 * Gated on an active brief: `load()` is a no-op when no case/brief is bound
 * (a fresh, unbound doc has nothing to show a history for). The derivation
 * (filter `snapshotOf === briefId` + sort desc) lives in the adapter — the
 * hook is pure orchestration (W1), no Office.js (W2).
 */

import { useCallback, useState } from 'react'
import { getContainer } from '@/container'
import { useCaseStore } from '@/ui/stores/use-case-store'
import type { VersionSnapshot } from '@/domain/versions/entity'

export interface UseVersionsResult {
  snapshots: VersionSnapshot[]
  loading: boolean
  error: Error | null
  /** Fetch the active brief's snapshot history. No-op when no brief is active. */
  load: () => Promise<void>
}

export function useVersions(): UseVersionsResult {
  const [snapshots, setSnapshots] = useState<VersionSnapshot[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const load = useCallback(async () => {
    const active = useCaseStore.getState().activeContext
    if (!active) return
    setLoading(true)
    setError(null)
    try {
      const { caseList } = getContainer()
      const rows = await caseList.listSnapshots(active.caseId, active.briefId)
      setSnapshots(rows)
    } catch (err: unknown) {
      const wrapped = err instanceof Error ? err : new Error(String(err))
      // eslint-disable-next-line no-console
      console.error('[useVersions] load failed', wrapped)
      setError(wrapped)
    } finally {
      setLoading(false)
    }
  }, [])

  return { snapshots, loading, error, load }
}
